import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  listNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  unreadNotificationsCount,
} from "@/lib/notifications";

const cn = (...xs) => xs.filter(Boolean).join(" ");

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.max(1, Math.round((Date.now() - d.getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const dd = Math.round(h / 24);
  return `${dd}d ago`;
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const nav = useNavigate();

  const refreshCount = async () => {
    try { setUnread(await unreadNotificationsCount()); } catch { /* ignore */ }
  };

  const refreshList = async () => {
    setLoading(true);
    try {
      const { items: list, unread: u } = await listNotifications({ limit: 20 });
      setItems(list); setUnread(u);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  // initial fetch + 45s polling
  useEffect(() => {
    refreshCount();
    const t = setInterval(refreshCount, 45000);
    // Also refresh when the reports-unread event fires (mark-read side effect)
    const handler = () => refreshCount();
    window.addEventListener("reports:unread-changed", handler);
    return () => { clearInterval(t); window.removeEventListener("reports:unread-changed", handler); };
  }, []);

  // Fetch list on open
  useEffect(() => { if (open) refreshList(); }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const onItemClick = async (n) => {
    setOpen(false);
    try {
      if (!n.read_at) {
        await markNotificationRead(n.id);
        setUnread((v) => Math.max(0, v - 1));
      }
    } catch { /* ignore */ }
    if (n.link) {
      // If report link, mirror the AppShell event too
      if (n.link.startsWith("/app/reports")) {
        window.dispatchEvent(new Event("reports:unread-changed"));
      }
      nav(n.link);
    }
  };

  const onMarkAll = async (e) => {
    e.stopPropagation();
    try {
      await markAllNotificationsRead();
      setItems((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
      setUnread(0);
      window.dispatchEvent(new Event("reports:unread-changed"));
    } catch { /* ignore */ }
  };

  return (
    <div className="relative" ref={wrapRef} data-testid="notif-bell-wrap">
      <button
        type="button"
        aria-label="Notifications"
        data-testid="notif-bell-btn"
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-lg text-[#0B1F3A] hover:bg-[#E8F1FF] transition-colors"
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </svg>
        {unread > 0 && (
          <span
            data-testid="notif-bell-badge"
            className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-[#DC2626] text-white"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="notif-panel"
          className="absolute right-0 top-12 w-[360px] max-w-[92vw] bg-white border border-[#0B1F3A]/10 rounded-xl shadow-xl z-50 overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#0B1F3A]/10">
            <div className="text-sm font-semibold text-[#0B1F3A]">Notifications</div>
            <button
              type="button"
              onClick={onMarkAll}
              data-testid="notif-mark-all"
              disabled={unread === 0}
              className={cn(
                "text-[11px] font-bold uppercase tracking-wider",
                unread === 0 ? "text-[#0B1F3A]/30 cursor-not-allowed" : "text-[#2D7FF9] hover:text-[#0B1F3A]"
              )}
            >Mark all read</button>
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {loading && (
              <div className="px-4 py-6 text-center text-sm text-[#0B1F3A]/60" data-testid="notif-loading">Loading…</div>
            )}
            {!loading && items.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-[#0B1F3A]/60" data-testid="notif-empty">
                You're all caught up.
              </div>
            )}
            {!loading && items.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => onItemClick(n)}
                data-testid={`notif-item-${n.id}`}
                className={cn(
                  "w-full text-left px-4 py-3 border-b border-[#0B1F3A]/5 hover:bg-[#E8F1FF] transition-colors flex gap-3",
                  !n.read_at && "bg-[#E8F1FF]/40"
                )}
              >
                <span className={cn(
                  "flex-shrink-0 mt-1.5 w-2 h-2 rounded-full",
                  !n.read_at ? "bg-[#2D7FF9]" : "bg-transparent"
                )} aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-[#0B1F3A] truncate">{n.title}</div>
                  {n.body && <div className="text-xs text-[#0B1F3A]/70 mt-0.5 line-clamp-2">{n.body}</div>}
                  <div className="text-[10px] text-[#0B1F3A]/50 mt-1 uppercase tracking-wider">{timeAgo(n.created_at)}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
